import { useCallback, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from 'store'
import { Permissions, PermissionActionType } from 'services/types/user'
import { getUserPermission } from 'services/user'
import { FETCHING_STATUS } from 'utils/constants'

const fetchStatusValues = Object.values(FETCHING_STATUS)
type PermissionStatus = typeof fetchStatusValues[number]

const useUserPermissions: (k: keyof Permissions) => [PermissionActionType, PermissionStatus, () => void] = (
  permissionKey,
) => {
  const dispatch = useAppDispatch()
  const userPermissions = useAppSelector((state) => state.user.userPermissions)

  const permissions: PermissionActionType = {
    canCreate: false,
    canView: false,
    canViewOtherAccount: false,
    canViewUnassignedLead: false,
    canEdit: false,
    canEditOtherAccount: false,
    canEditUnassignedLead: false,
    canDelete: false,
    canDeactivate: false,
    canAssignPermission: false,
    canResetPassword: false,
    canApprove: false,
    canAssignAdmin: false,
    canImport: false,
    canExport: false,
    canAssign: false,
    canAssignMeetingOtherUser: false,
    canLeadOwnerAssignToOther: false,
    canMerge: false,
    canAnything: false,
    canUpdateLeadCampaign: false,
    canUpdateLeadSource: false,
    canRestoreInvalidLeads: false,
    canRestoreLostLeads: false,
    canRestoreDeletedLeads: false,
    canSkipMeetingMinuteEmail: false,
    canExportOpportunity: false,
    canViewTeam: false,
    canEditTeam: false,
    canEditMeetingHistoryLocation: false,
    canEditCallLog: false,
    canDeleteCallLog: false,
    canEditLiveCall: false,
    canCreateOpportunity: false,
    canViewOpportunitySelfCreated: false,
    canViewOpportunitySelfOwned: false,
    canViewOpportunityAllOther: false,
    canEditCallBackTaskToggleDefaultState: false,
    canEditCallBackTaskDefaultAssignee: false,
    canEditCallBackTaskDefaultDateTime: false,
    canEditCallTaskAssignee: false,
    canEditCallTaskDueDateTime: false,
    canEditCallbackRouting: false,
    canEditLeadEmailLog: false,
    canDeleteLeadEmailLog: false,
    canEditMessageLog: false,
    canDeleteMessageLog: false,
    canManualLeadStageUpdate: false,
    canManualCloseLead: false,
    canEditAccount: false,
    canEditConvertedLead: false,
    userActivityAudit: false,
    canDeleteAccount: false,
    canExportLeadContacts: false,
    canExportLeadAddresses: false,
    canExportLeadActivities: false,
    canAssignUnassignedLeadToOther: false,
    canLeadOwnerTeamAssignToOther: false,
    canLeadCollaboratorAssignToOther: false,
    hasAssignLeadToOther: false,
    canViewDeletedLeads: false,
    canEditLostInvalidLeads: false,
    canAssignCustomLeadDetailLayout: false,
    canDeleteCustomLeadDetailLayout: false,
    canEditAndCreateCustomLeadDetailLayout: false,
    canViewCustomLeadDetailLayout: false,
    canPersonalizeLeadDetailPageLayout: false,
  }

  const fetchData = useCallback(() => {
    dispatch(getUserPermission())
  }, [dispatch])

  useEffect(() => {
    if (userPermissions.status === FETCHING_STATUS.unSet) {
      fetchData()
    }
  }, [fetchData, userPermissions.status])

  if (userPermissions.status === FETCHING_STATUS.success && userPermissions.data[permissionKey]) {
    Object.keys(permissions).forEach((key) => {
      const data = userPermissions.data[permissionKey] as PermissionActionType
      if (data[key as keyof PermissionActionType]) {
        permissions[key as keyof PermissionActionType] = true
      }
    })

    permissions.hasAssignLeadToOther =
      permissions.canLeadOwnerAssignToOther ||
      permissions.canLeadCollaboratorAssignToOther ||
      permissions.canLeadOwnerTeamAssignToOther ||
      permissions.canAssignUnassignedLeadToOther

    return [permissions, userPermissions.status, fetchData]
  }

  return [permissions, userPermissions.status, fetchData]
}

export default useUserPermissions
